import { useRef } from 'react';

type ScopeRegistrationFingerprintInput = {
  storedStepIds: string[] | null | undefined;
  showScopeControls: boolean;
  canManageScope: boolean;
};

const PREFIX = '[SCOPE-LOOP]';
const LOOP_WINDOW_MS = 500;
const LOOP_THRESHOLD = 8;
const RING_BUFFER_SIZE = 200;
const ASYNC_STORAGE_KEY = '@dev/scope-loop-log';

const objectRefIds = new WeakMap<object, number>();
let nextObjectRefId = 1;

type RingEntry = {
  ts: number;
  label: string;
  data?: Record<string, unknown>;
};

const ringBuffer: RingEntry[] = [];

function pushRingEntry(label: string, data?: Record<string, unknown>): void {
  ringBuffer.push({ ts: Date.now(), label, data });
  if (ringBuffer.length > RING_BUFFER_SIZE) {
    ringBuffer.splice(0, ringBuffer.length - RING_BUFFER_SIZE);
  }
}

function dumpScopeLoopLog(): string {
  const lines = ringBuffer.map(entry => {
    const dataPart = entry.data ? ` ${JSON.stringify(entry.data)}` : '';
    return `${new Date(entry.ts).toISOString()} ${PREFIX} ${
      entry.label
    }${dataPart}`;
  });
  const dump = lines.join('\n');
  // eslint-disable-next-line no-console
  console.log(`${PREFIX} dump (${ringBuffer.length} entries):\n${dump}`);
  return dump;
}

function persistRingBufferBurst(reason: string): void {
  if (!isScopeLoopDebugEnabled() || ringBuffer.length === 0) {
    return;
  }
  try {
    // Lazy require — dev-only; avoid pulling AsyncStorage into production bundles.

    const AsyncStorage = require('@react-native-async-storage/async-storage')
      .default as {
      setItem: (key: string, value: string) => Promise<void>;
    };
    const payload = JSON.stringify({
      reason,
      savedAt: new Date().toISOString(),
      entries: ringBuffer,
    });
    void AsyncStorage.setItem(ASYNC_STORAGE_KEY, payload).catch(() => {
      /* dev-only best effort */
    });
  } catch {
    /* AsyncStorage unavailable (e.g. web test env) */
  }
}

function logLoopDetected(
  key: string,
  count: number,
  fingerprint: string,
): void {
  const instruction =
    'Loop detected — call global.__scopeLoopLogDump() in DevTools to recover recent [SCOPE-LOOP] logs after crash/reload.';
  console.error(
    `${PREFIX} LOOP DETECTED key=${key} count=${count} windowMs=${LOOP_WINDOW_MS} fingerprint=${fingerprint}`,
    new Error(`${key} scope loop`).stack,
  );
  console.error(`${PREFIX} ${instruction}`);
  persistRingBufferBurst(`loop:${key}`);
}

function getObjectRefId(value: object): number {
  let id = objectRefIds.get(value);
  if (id == null) {
    id = nextObjectRefId++;
    objectRefIds.set(value, id);
  }
  return id;
}

export function isScopeLoopDebugEnabled(): boolean {
  return typeof __DEV__ !== 'undefined' && __DEV__;
}

export function scopeLoopLog(
  label: string,
  data?: Record<string, unknown>,
): void {
  if (!isScopeLoopDebugEnabled()) {
    return;
  }
  pushRingEntry(label, data);
  if (data) {
    console.log(`${PREFIX} ${label}`, data);
  } else {
    console.log(`${PREFIX} ${label}`);
  }
}

export function summarizeStepIds(
  ids: string[] | null | undefined,
): Record<string, unknown> {
  if (ids == null) {
    return { kind: String(ids), length: null };
  }
  return {
    kind: 'array',
    refId: getObjectRefId(ids),
    length: ids.length,
    preview:
      ids.length <= 4
        ? ids.join(',')
        : `${ids.slice(0, 3).join(',')}…+${ids.length - 3}`,
  };
}

export function summarizeDepValue(value: unknown): unknown {
  if (value == null || typeof value !== 'object') {
    if (typeof value === 'function') {
      return `fn@${getObjectRefId(value)}`;
    }
    return value;
  }
  if (Array.isArray(value)) {
    return summarizeStepIds(value);
  }
  return `obj@${getObjectRefId(value)}`;
}

export function registrationFingerprint(
  registration: ScopeRegistrationFingerprintInput | null,
): string {
  if (!registration) {
    return 'null';
  }
  const ids = registration.storedStepIds;
  const idsPart =
    ids == null
      ? String(ids)
      : `array@${getObjectRefId(ids)}:len=${ids.length}`;
  return `${idsPart}|show:${registration.showScopeControls}|manage:${registration.canManageScope}`;
}

interface LoopWindowState {
  count: number;
  windowStart: number;
  lastFingerprint: string | null;
  loggedBurst: boolean;
}

const loopWindows = new Map<string, LoopWindowState>();

/** Dev-only burst detector; may skip redundant identical calls when looping. */
export function checkScopeLoop(
  key: string,
  fingerprint: string,
): { loopDetected: boolean; shouldSkip: boolean } {
  if (!isScopeLoopDebugEnabled()) {
    return { loopDetected: false, shouldSkip: false };
  }

  const now = Date.now();
  let state = loopWindows.get(key);
  if (!state || now - state.windowStart > LOOP_WINDOW_MS) {
    loopWindows.set(key, {
      count: 1,
      windowStart: now,
      lastFingerprint: fingerprint,
      loggedBurst: false,
    });
    return { loopDetected: false, shouldSkip: false };
  }

  const duplicate = state.lastFingerprint === fingerprint;
  state.count += 1;
  state.lastFingerprint = fingerprint;

  if (state.count > LOOP_THRESHOLD) {
    if (!state.loggedBurst) {
      state.loggedBurst = true;
      logLoopDetected(key, state.count, fingerprint);
    }
    return { loopDetected: true, shouldSkip: duplicate };
  }

  return { loopDetected: false, shouldSkip: false };
}

export function logScopeEffectDeps(
  effectName: string,
  deps: Record<string, unknown>,
  prevDepsRef: { current: Record<string, unknown> | null },
): readonly string[] {
  if (!isScopeLoopDebugEnabled()) {
    prevDepsRef.current = deps;
    return [];
  }

  const prev = prevDepsRef.current;
  const changed: string[] = [];
  if (prev) {
    for (const key of Object.keys(deps)) {
      if (!Object.is(prev[key], deps[key])) {
        changed.push(key);
      }
    }
  } else {
    changed.push('(initial mount)');
  }
  prevDepsRef.current = deps;

  const serializedDeps: Record<string, unknown> = {};
  for (const [key, value] of Object.entries(deps)) {
    serializedDeps[key] = summarizeDepValue(value);
  }

  scopeLoopLog(`${effectName} effect fired`, {
    changed,
    deps: serializedDeps,
  });

  return changed;
}

export function useScopeLoopRenderCounter(componentName: string): number {
  const renderCountRef = useRef(0);
  renderCountRef.current += 1;

  if (isScopeLoopDebugEnabled()) {
    const count = renderCountRef.current;
    if (count === 1) {
      scopeLoopLog(`${componentName} mount`);
    } else if (count <= 25 || count % 10 === 0) {
      scopeLoopLog(`${componentName} render`, { count });
    }
    if (count === 51) {
      const instruction =
        'Render storm — call global.__scopeLoopLogDump() in DevTools to recover recent [SCOPE-LOOP] logs.';
      console.error(
        `${PREFIX} ${componentName} render storm (>50 renders)`,
        new Error(`${componentName} render storm`).stack,
      );
      console.error(`${PREFIX} ${instruction}`);
      persistRingBufferBurst(`render-storm:${componentName}`);
    }
  }

  return renderCountRef.current;
}

type ScopeLoopGlobal = {
  __scopeLoopDebugReset?: () => void;
  __scopeLoopLogDump?: () => string;
};

function getScopeLoopGlobal(): ScopeLoopGlobal | undefined {
  if (typeof globalThis !== 'undefined') {
    return globalThis as ScopeLoopGlobal;
  }
  return undefined;
}

export function registerScopeLoopDebugGlobals(): void {
  if (!isScopeLoopDebugEnabled()) {
    return;
  }
  const g = getScopeLoopGlobal();
  if (!g) {
    return;
  }
  g.__scopeLoopDebugReset = () => {
    loopWindows.clear();
    ringBuffer.length = 0;
    scopeLoopLog('loop detector reset');
  };
  g.__scopeLoopLogDump = () => dumpScopeLoopLog();
  scopeLoopLog(
    'debug ON — filter console by [SCOPE-LOOP]; call global.__scopeLoopLogDump() or global.__scopeLoopDebugReset()',
  );
}

if (isScopeLoopDebugEnabled()) {
  registerScopeLoopDebugGlobals();
}
